import { useState, useEffect } from "react";
import { costsAPI, ordersAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Calculator,
  MessageCircle,
  Globe,
  Music,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Percent,
  Settings,
  RefreshCw,
  Package,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";

const Scorporo = () => {
  const [costs, setCosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [channelData, setChannelData] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState("");

  useEffect(() => {
    loadCosts();
  }, []);

  const loadCosts = async () => {
    try {
      const res = await costsAPI.get();
      setCosts(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei costi");
    } finally {
      setLoading(false);
    }
  };

  const calculateChannelScorporo = async () => {
    if (!selectedChannel) {
      toast.error("Seleziona un canale");
      return;
    }
    
    setCalculating(true);
    try {
      // Get orders for the selected channel (limit to recent for performance)
      const res = await ordersAPI.getAll({ channel: selectedChannel, limit: 2000 });
      const orders = res.data.orders || res.data || [];
      
      // Calculate totals
      let totalRevenue = 0;
      let totalItems = 0;
      let orderCount = 0;
      
      orders.forEach(order => {
        if (order.channel === selectedChannel) {
          totalRevenue += order.total_amount || 0;
          totalItems += order.total_items || order.items?.length || 0;
          orderCount++;
        }
      });
      
      // Calculate costs based on settings
      const vatPercent = costs?.vat_percent || 22;
      const vatAmount = totalRevenue * (vatPercent / (100 + vatPercent));
      const revenueExVat = totalRevenue - vatAmount;
      
      // Apply custom costs
      let totalCustomCosts = 0;
      const costDetails = [];
      
      if (costs?.costs) {
        costs.costs.forEach(cost => {
          let costValue = 0;
          if (cost.is_percentage) {
            costValue = revenueExVat * (cost.value / 100);
          } else {
            costValue = cost.value * orderCount; // Fixed cost per order
          }
          totalCustomCosts += costValue;
          costDetails.push({
            name: cost.name,
            value: costValue,
            is_percentage: cost.is_percentage
          });
        });
      }
      
      // Estimate product cost (rough estimate based on typical margin)
      const estimatedProductCost = revenueExVat * 0.3; // 30% product cost estimate
      
      const totalCosts = vatAmount + totalCustomCosts + estimatedProductCost;
      const netProfit = totalRevenue - totalCosts;
      const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;
      
      setChannelData({
        channel: selectedChannel,
        totalRevenue,
        orderCount,
        totalItems,
        vatAmount,
        revenueExVat,
        estimatedProductCost,
        customCosts: totalCustomCosts,
        costDetails,
        totalCosts,
        netProfit,
        profitMargin
      });
      
    } catch (error) {
      toast.error("Errore nel calcolo");
      console.error(error);
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value || 0);
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case "whatsapp":
        return <MessageCircle className="w-5 h-5 text-green-500" />;
      case "website":
        return <Globe className="w-5 h-5 text-blue-500" />;
      case "tiktok_shop":
        return <Music className="w-5 h-5 text-pink-500" />;
      default:
        return <Package className="w-5 h-5 text-gray-500" />;
    }
  };

  const getChannelLabel = (channel) => {
    switch (channel) {
      case "whatsapp":
        return "WhatsApp";
      case "website":
        return "Sito Web";
      case "tiktok_shop":
        return "TikTok Shop";
      default:
        return channel;
    }
  };

  const getChannelColor = (channel) => {
    switch (channel) {
      case "whatsapp":
        return "bg-green-500";
      case "website":
        return "bg-blue-500";
      case "tiktok_shop":
        return "bg-gradient-to-r from-pink-500 to-purple-500";
      default:
        return "bg-gray-500";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#C9A227] animate-pulse">Caricamento...</div>
      </div>
    );
  }

  return (
    <div data-testid="scorporo-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-serif text-gray-900 mb-2">
          Scorporo per Canale
        </h1>
        <p className="text-gray-500">
          Analizza costi e guadagni totali divisi per canale di vendita
        </p>
      </div>

      {/* Channel Selection */}
      <Card className="bg-white/60 border-[#C9A227]/15">
        <CardHeader>
          <CardTitle className="text-lg font-serif text-gray-900 flex items-center gap-2">
            <Calculator className="w-5 h-5 text-[#C9A227]" />
            Seleziona Canale
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={selectedChannel} onValueChange={setSelectedChannel}>
              <SelectTrigger className="flex-1 bg-gray-50 border-gray-200" data-testid="channel-select">
                <SelectValue placeholder="Seleziona canale di vendita" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="whatsapp">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-green-500" />
                    WhatsApp
                  </div>
                </SelectItem>
                <SelectItem value="website">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-blue-500" />
                    Sito Web
                  </div>
                </SelectItem>
                <SelectItem value="tiktok_shop">
                  <div className="flex items-center gap-2">
                    <Music className="w-4 h-4 text-pink-500" />
                    TikTok Shop
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              onClick={calculateChannelScorporo}
              disabled={calculating || !selectedChannel}
              className="bg-[#C9A227] text-black hover:bg-[#FFD700]"
              data-testid="calculate-btn"
            >
              {calculating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Calcolo...
                </>
              ) : (
                <>
                  <Calculator className="w-4 h-4 mr-2" />
                  Scorpora Canale
                </>
              )}
            </Button>
          </div>
          
          {/* Costs Info */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-gray-600 font-medium">Costi applicati:</p>
              <Link to="/settings">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-[#C9A227] hover:bg-amber-50 h-7 px-2"
                >
                  <Settings className="w-3 h-3 mr-1" />
                  Modifica
                </Button>
              </Link>
            </div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">IVA:</span>
                <span className="text-gray-900 font-medium">{costs?.vat_percent || 22}%</span>
              </div>
              {costs?.costs?.map((cost, index) => (
                <div key={index} className="flex justify-between">
                  <span className="text-gray-600">{cost.name}:</span>
                  <span className="text-gray-900 font-medium">
                    {cost.is_percentage ? `${cost.value}%` : formatCurrency(cost.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {channelData && (
        <div className="space-y-4">
          {/* Annulla Button */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setChannelData(null);
                setSelectedChannel("");
              }}
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid="reset-scorporo-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Annulla Scorporo
            </Button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Summary Card */}
          <Card className="bg-white/60 border-[#C9A227]/15">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl ${getChannelColor(channelData.channel)} flex items-center justify-center`}>
                  {getChannelIcon(channelData.channel)}
                </div>
                <div>
                  <CardTitle className="text-lg font-serif text-gray-900">
                    {getChannelLabel(channelData.channel)}
                  </CardTitle>
                  <p className="text-sm text-gray-500">Riepilogo totale</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-900">{channelData.orderCount}</p>
                  <p className="text-sm text-gray-500">Ordini</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-900">{channelData.totalItems}</p>
                  <p className="text-sm text-gray-500">Articoli</p>
                </div>
              </div>
              
              {/* Total Revenue */}
              <div className="p-4 bg-[#C9A227]/10 rounded-lg border border-[#C9A227]/20">
                <div className="flex items-center justify-between">
                  <span className="text-[#C9A227] font-medium">Fatturato Totale</span>
                  <span className="text-2xl font-bold text-[#C9A227]">
                    {formatCurrency(channelData.totalRevenue)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cost Breakdown */}
          <Card className="bg-white/60 border-[#C9A227]/15">
            <CardHeader>
              <CardTitle className="text-lg font-serif text-gray-900">
                Dettaglio Costi
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* VAT */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-gray-600">
                  <Percent className="w-4 h-4" />
                  <span>IVA ({costs?.vat_percent || 22}%)</span>
                </div>
                <span className="text-gray-900 font-medium">
                  -{formatCurrency(channelData.vatAmount)}
                </span>
              </div>
              
              {/* Product Cost Estimate */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-gray-600">
                  <Package className="w-4 h-4" />
                  <span>Costo Prodotti (stima 30%)</span>
                </div>
                <span className="text-gray-900 font-medium">
                  -{formatCurrency(channelData.estimatedProductCost)}
                </span>
              </div>
              
              {/* Custom Costs */}
              {channelData.costDetails?.map((cost, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600">
                    <DollarSign className="w-4 h-4" />
                    <span>{cost.name}</span>
                  </div>
                  <span className="text-gray-900 font-medium">
                    -{formatCurrency(cost.value)}
                  </span>
                </div>
              ))}
              
              {/* Total Costs */}
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-red-600">
                    <TrendingDown className="w-5 h-5" />
                    <span className="font-medium">Totale Costi</span>
                  </div>
                  <span className="text-xl font-bold text-red-600">
                    -{formatCurrency(channelData.totalCosts)}
                  </span>
                </div>
              </div>
              
              {/* Net Profit */}
              <div className={`p-4 rounded-lg border ${
                channelData.netProfit >= 0 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className={`flex items-center gap-2 ${
                    channelData.netProfit >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    <TrendingUp className="w-5 h-5" />
                    <span className="font-medium">Guadagno Netto</span>
                  </div>
                  <span className={`text-2xl font-bold ${
                    channelData.netProfit >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(channelData.netProfit)}
                  </span>
                </div>
                <div className="mt-2 text-right">
                  <span className={`text-sm ${
                    channelData.netProfit >= 0 ? 'text-green-500' : 'text-red-500'
                  }`}>
                    Margine: {channelData.profitMargin.toFixed(1)}%
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      )}

      {/* Empty State */}
      {!channelData && (
        <Card className="bg-white/60 border-[#C9A227]/15">
          <CardContent className="py-16">
            <div className="flex flex-col items-center justify-center text-gray-500">
              <Calculator className="w-16 h-16 mb-4 opacity-20" />
              <p className="text-lg">Seleziona un canale e clicca "Scorpora Canale"</p>
              <p className="text-sm mt-2">per vedere i totali aggregati di costi e guadagni</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Scorporo;
